// JURÍDICO — cadastro dos tipos de contrato (2026-09-15).
//
// Pedido do Lucas: "preciso conseguir criar e também fazer o upload de outros
// modelos de contrato". O tipo cadastrado aqui aparece no "Redigir contrato" e
// no "Incluir contrato assinado" ao mesmo tempo — é um catálogo só.
//
// O modelo de texto pode ser escrito aqui ou GERADO de um arquivo: solta-se um
// contrato (o mútuo assinado, uma minuta de advogado em Word) e a IA devolve o
// texto com os dados daquele contrato em branco. Chega preenchido, não salvo:
// quem decide o que fica é quem está olhando.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, App as AntApp, Button, Checkbox, Empty, Flex, Form, Input, Modal, Space, Spin,
  Table, Tag, Tooltip, Typography, Upload,
} from 'antd';
import RichTextEditor from '../RichTextEditor';
import {
  listarTiposContrato, salvarTipoContrato, subirContratoAssinado, lerContrato, statusLeitura,
  rotulosPartes, TipoContrato, TIPO_DISTRATO,
} from './api';

const { Text, Paragraph } = Typography;

// Leitura de contrato longo leva até ~2 minutos para devolver o texto inteiro.
const TETO_ESPERA_MS = 5 * 60 * 1000;

/** O corpo do erro pode vir embrulhado em `body` (string) — é assim que a
 *  layer serializa, e o wrapper só desembrulha no caminho feliz. */
function lerErro(e: any): { status?: number; dados: any } {
  const bruto = e?.response?.data;
  let dados: any = bruto;
  if (bruto?.body) { try { dados = JSON.parse(bruto.body); } catch { dados = bruto; } }
  return { status: e?.response?.status, dados: dados || {} };
}

type Editando = TipoContrato | 'novo' | null;

export function ModalTiposContrato({ aberto, onFechar, onMudou }: {
  aberto: boolean;
  onFechar: () => void;
  onMudou: () => void;
}) {
  const { message, modal } = AntApp.useApp();
  const [form] = Form.useForm();
  const [tipos, setTipos] = useState<TipoContrato[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [editando, setEditando] = useState<Editando>(null);
  const [salvando, setSalvando] = useState(false);

  // ⚠️ O texto vive em DOIS lugares: `corpo` alimenta o editor e `corpoRef` é o
  //    que o salvar envia. O RichTextEditor aplica valor externo com
  //    emitUpdate:false — escrever só no state não dispara o onChange, e o
  //    salvar mandaria o texto velho (a pegadinha da Pam no EditorExpediente).
  const [corpo, setCorpo] = useState('<p></p>');
  const corpoRef = useRef('<p></p>');
  const [corpoMexido, setCorpoMexido] = useState(false);

  const [gerando, setGerando] = useState<null | 'subindo' | 'lendo'>(null);
  const [avisoModelo, setAvisoModelo] = useState<{ truncado: boolean; removidos: number; arquivo: string } | null>(null);
  const pollRef = useRef<number | null>(null);
  // Cada geração e cada troca de formulário ganha um número. A resposta de uma
  // geração antiga (a pessoa voltou para a lista e abriu OUTRO tipo) não pode
  // cair no formulário de agora — ela levaria o texto do mútuo para o seguro.
  const geracaoRef = useRef(0);
  // O editor aplica valor externo sem avisar; remontá-lo ao trocar de tipo
  // garante que ele mostre o texto do tipo aberto, e não o do anterior.
  const [versaoEditor, setVersaoEditor] = useState(0);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await listarTiposContrato({ incluir_inativos: true });
      setTipos(r.tipos || []);
    } catch (e: any) {
      message.error('Falha ao carregar os tipos: ' + (lerErro(e).dados?.error || e.message));
    } finally { setCarregando(false); }
  }, [message]);

  useEffect(() => { if (aberto) carregar(); }, [aberto, carregar]);
  useEffect(() => () => { if (pollRef.current) window.clearInterval(pollRef.current); }, []);

  function definirCorpo(html: string) {
    setCorpo(html);
    corpoRef.current = html;
  }

  function pararGeracao() {
    geracaoRef.current += 1;
    if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
    setGerando(null);
  }

  function abrirForm(t: Editando) {
    pararGeracao();
    setVersaoEditor((n) => n + 1);
    form.resetFields();
    setAvisoModelo(null);
    setCorpoMexido(false);
    if (t && t !== 'novo') {
      form.setFieldsValue({
        nome: t.nome, prefixo: t.prefixo, descricao: t.descricao || '',
        rotulo_contratante: t.rotulo_contratante || '', rotulo_contratada: t.rotulo_contratada || '',
        sensivel: t.sensivel, redigivel: t.redigivel, ativo: t.ativo,
      });
      definirCorpo(t.corpo_padrao_html || '<p></p>');
    } else {
      form.setFieldsValue({ sensivel: false, redigivel: true, ativo: true });
      definirCorpo('<p></p>');
    }
    setEditando(t);
  }

  function voltarParaLista() {
    pararGeracao();
    setEditando(null);
  }

  // Sair do formulário com coisa por salvar pergunta antes: o modelo gerado
  // custou uma leitura da IA e minutos de conferência.
  async function sairComCuidado(depois: () => void) {
    const mexeu = editando !== null && (corpoMexido || !!gerando || form.isFieldsTouched());
    if (mexeu) {
      const seguir = await modal.confirm({
        title: gerando ? 'Parar a geração do modelo?' : 'Sair sem salvar?',
        content: gerando
          ? 'A IA ainda está montando o texto. Saindo agora, o resultado se perde.'
          : 'O que foi preenchido neste tipo não foi salvo.',
        okText: 'Sair sem salvar', cancelText: 'Continuar aqui', okButtonProps: { danger: true },
      });
      if (!seguir) return;
    }
    depois();
  }

  // ── Gerar o modelo a partir de um arquivo ────────────────────────────────
  async function gerarModelo(arquivo: File) {
    if (gerando) return;
    const temTexto = corpoRef.current.replace(/<[^>]*>/g, '').trim().length > 0;
    if (temTexto) {
      const seguir = await modal.confirm({
        title: 'Trocar o texto do modelo?',
        content: 'O texto que está no editor será substituído pelo que a IA montar a partir do arquivo.',
        okText: 'Trocar', cancelText: 'Manter o atual',
      });
      if (!seguir) return;
    }
    setAvisoModelo(null);
    const geracao = ++geracaoRef.current;
    const ehAtual = () => geracao === geracaoRef.current;
    try {
      setGerando('subindo');
      const s3_key = await subirContratoAssinado(arquivo);
      setGerando('lendo');
      if (!ehAtual()) return;
      const r = await lerContrato({ s3_key, arquivo_nome: arquivo.name, finalidade: 'modelo' });
      if (!ehAtual()) return;
      // O que estiver no editor quando a leitura começa. Se a pessoa escrever
      // enquanto espera, a troca pelo texto da IA pergunta antes.
      const corpoNoInicio = corpoRef.current;
      const inicio = Date.now();
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = window.setInterval(async () => {
        if (!ehAtual()) return;
        if (Date.now() - inicio > TETO_ESPERA_MS) {
          if (pollRef.current) window.clearInterval(pollRef.current);
          pollRef.current = null;
          setGerando(null);
          message.error('A leitura demorou demais. Tente de novo ou escreva o modelo à mão.');
          return;
        }
        try {
          const { leitura } = await statusLeitura(r.leitura_id);
          if (!ehAtual() || leitura.status === 'processando') return;
          if (pollRef.current) window.clearInterval(pollRef.current);
          pollRef.current = null;
          setGerando(null);
          if (leitura.status === 'erro') {
            message.error(leitura.erro || 'Não consegui montar o modelo desse arquivo.');
            return;
          }
          const c = leitura.campos || {};
          if (!ehAtual()) return;
          // O que a pessoa já digitou manda: a IA só preenche o que está vazio.
          const atual = form.getFieldsValue();
          const patch: Record<string, any> = {};
          if (!String(atual.nome || '').trim() && c.nome_sugerido) patch.nome = c.nome_sugerido;
          if (!String(atual.prefixo || '').trim() && c.prefixo_sugerido) patch.prefixo = c.prefixo_sugerido;
          if (!String(atual.descricao || '').trim() && c.descricao) patch.descricao = c.descricao;
          // Nomes das partes só em tipo SEM contratos: os nomes valem por
          // posição, e a IA pode ler "Mutuário/Mutuante" na ordem inversa —
          // num tipo em uso isso inverteria o lado das fichas que já existem.
          const semFichas = editando === 'novo' || (editando as TipoContrato)?.em_uso === 0;
          if (semFichas
              && !String(atual.rotulo_contratante || '').trim() && !String(atual.rotulo_contratada || '').trim()
              && c.rotulo_contratante && c.rotulo_contratada) {
            patch.rotulo_contratante = c.rotulo_contratante;
            patch.rotulo_contratada = c.rotulo_contratada;
          }
          // Sigilo sugerido só LIGA, e só em tipo novo: desligar sigilo é
          // decisão de quem administra, nunca palpite de leitura.
          if (editando === 'novo' && c.sensivel_sugerido === true) patch.sensivel = true;
          form.setFieldsValue(patch);
          if (c.corpo_html) {
            let trocar = true;
            if (corpoRef.current !== corpoNoInicio) {
              trocar = await modal.confirm({
                title: 'Você escreveu no modelo enquanto a IA lia',
                content: 'Trocar o que está no editor pelo texto montado a partir do arquivo?',
                okText: 'Trocar', cancelText: 'Manter o que escrevi',
              });
            }
            if (trocar && ehAtual()) {
              definirCorpo(c.corpo_html);
              setVersaoEditor((n) => n + 1);
              setCorpoMexido(true);
            }
          }
          setAvisoModelo({
            truncado: !!c.truncado,
            removidos: Number(c.dados_removidos_pelo_filtro) || 0,
            arquivo: arquivo.name,
          });
        } catch { /* rede oscilou: tenta no próximo tique */ }
      }, 3000);
    } catch (e: any) {
      setGerando(null);
      message.error('Falha ao enviar o arquivo: ' + (lerErro(e).dados?.error || e.message));
    }
  }

  // ── Salvar ──────────────────────────────────────────────────────────────
  async function salvar(confirmacoes: { confirmar_rotulos?: boolean; confirmar_sigilo?: boolean } = {}) {
    let v: any;
    try { v = await form.validateFields(); } catch { return; }
    const ehNovo = editando === 'novo';
    const payload: Record<string, any> = {
      ...(ehNovo ? {} : { id: (editando as TipoContrato).id }),
      nome: v.nome,
      prefixo: String(v.prefixo || '').toUpperCase(),
      descricao: v.descricao || null,
      rotulo_contratante: v.rotulo_contratante || null,
      rotulo_contratada: v.rotulo_contratada || null,
      sensivel: !!v.sensivel,
      redigivel: !!v.redigivel,
      ...(ehNovo ? {} : { ativo: !!v.ativo }),
      // Na edição, o texto só vai se foi mexido: reenviar o corpo inteiro a
      // cada ajuste de nome passaria o HTML de novo pelo filtro sem motivo.
      ...(ehNovo || corpoMexido ? { corpo_padrao_html: corpoRef.current } : {}),
      ...confirmacoes,
    };
    setSalvando(true);
    try {
      await salvarTipoContrato(payload);
      message.success(ehNovo ? `Tipo "${v.nome}" cadastrado.` : 'Tipo atualizado.');
      voltarParaLista();
      carregar();
      onMudou();
    } catch (e: any) {
      const { status, dados } = lerErro(e);
      if (status === 409 && dados?.confirmar) {
        const seguir = await modal.confirm({
          title: dados.confirmar === 'sigilo' ? 'Tirar o sigilo deste tipo?' : 'Mudar os nomes das partes?',
          content: dados.error,
          okText: 'Confirmo', cancelText: 'Voltar',
          okButtonProps: { danger: dados.confirmar === 'sigilo' },
        });
        if (seguir) {
          await salvar({
            ...confirmacoes,
            ...(dados.confirmar === 'rotulos' ? { confirmar_rotulos: true } : {}),
            ...(dados.confirmar === 'sigilo' ? { confirmar_sigilo: true } : {}),
          });
        }
        return;
      }
      message.error('Não salvou: ' + (dados?.error || e.message));
    } finally { setSalvando(false); }
  }

  const colunas = [
    {
      title: 'Tipo', key: 'nome',
      render: (_: any, t: TipoContrato) => (
        <Space direction="vertical" size={0}>
          <Text strong={t.ativo} type={t.ativo ? undefined : 'secondary'}>{t.nome}</Text>
          {t.descricao && <Text type="secondary" style={{ fontSize: 11 }}>{t.descricao}</Text>}
        </Space>
      ),
    },
    { title: 'Sigla', dataIndex: 'prefixo', key: 'prefixo', width: 90,
      render: (p: string) => <Tag>{p}</Tag> },
    {
      title: 'Partes', key: 'partes', width: 220,
      render: (_: any, t: TipoContrato) => {
        const r = rotulosPartes(t);
        return <Text style={{ fontSize: 12 }}>{r.contratante} · {r.contratada}</Text>;
      },
    },
    {
      title: 'Uso', key: 'uso', width: 190,
      render: (_: any, t: TipoContrato) => (
        <Space size={4} wrap>
          {t.sensivel && (
            <Tooltip title="Tem dado pessoal: só quem participa ou arquivou vê a contraparte e o valor.">
              <Tag color="purple">sigiloso</Tag>
            </Tooltip>
          )}
          {!t.redigivel && t.codigo !== TIPO_DISTRATO && <Tag>só upload</Tag>}
          {t.codigo === TIPO_DISTRATO && <Tag color="volcano">fluxo próprio</Tag>}
          {!t.ativo && <Tag color="default">desativado</Tag>}
          <Text type="secondary" style={{ fontSize: 11 }}>{t.em_uso ?? 0} contrato(s)</Text>
        </Space>
      ),
    },
    {
      title: '', key: 'acoes', width: 90,
      render: (_: any, t: TipoContrato) => (
        <Button size="small" onClick={() => abrirForm(t)}>Editar</Button>
      ),
    },
  ];

  const tipoEditado = editando && editando !== 'novo' ? editando : null;
  const ehDistrato = tipoEditado?.codigo === TIPO_DISTRATO;
  const emUso = tipoEditado?.em_uso || 0;

  return (
    <Modal
      open={aberto}
      maskClosable={false}
      onCancel={() => sairComCuidado(() => { voltarParaLista(); onFechar(); })}
      footer={null}
      title={editando === null ? 'Tipos de contrato'
        : editando === 'novo' ? 'Novo tipo de contrato' : `Tipo — ${tipoEditado?.nome}`}
      width="94vw" style={{ maxWidth: 1200, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
      destroyOnHidden
    >
      {editando === null && (
        <>
          <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 10 }}>
            <Paragraph type="secondary" style={{ fontSize: 12, margin: 0, maxWidth: 760 }}>
              Cada tipo aparece em <b>Redigir contrato</b> e em <b>Incluir contrato assinado</b>, com a
              sigla na numeração (MUT-2026-001) e o nome das partes na ficha e na assinatura.
            </Paragraph>
            <Button type="primary" onClick={() => abrirForm('novo')}>Novo tipo</Button>
          </Flex>
          <Table rowKey="id" size="small" loading={carregando} dataSource={tipos}
            columns={colunas as any} pagination={false} tableLayout="fixed"
            locale={{ emptyText: <Empty description="Nenhum tipo cadastrado" /> }} />
        </>
      )}

      {editando !== null && (
        <Flex gap={20} wrap align="flex-start">
          {/* Coluna dos dados — ao lado do texto, para o editor usar a altura da tela. */}
          <div style={{ flex: '1 1 320px', minWidth: 280, maxWidth: 420 }}>
            <Form form={form} layout="vertical" size="small">
              <Form.Item name="nome" label="Nome do tipo"
                rules={[{ required: true, min: 3, message: 'Dê um nome (ex.: Contrato de mútuo)' }]}>
                <Input placeholder="Ex.: Contrato de mútuo" maxLength={120} />
              </Form.Item>
              <Form.Item name="prefixo" label="Sigla"
                tooltip="Entra no número do contrato redigido aqui: MUT-2026-001."
                normalize={(v: string) => String(v || '').toUpperCase().replace(/[^A-Z]/g, '')}
                rules={[{ required: true, pattern: /^[A-Z]{2,8}$/, message: 'De 2 a 8 letras, sem acento' }]}>
                <Input placeholder="MUT" maxLength={8} style={{ width: 140 }} disabled={ehDistrato} />
              </Form.Item>
              <Form.Item name="descricao" label="Para que serve">
                <Input.TextArea rows={2} maxLength={500} placeholder="Ex.: Empréstimo da Hydrodata a pessoa física" />
              </Form.Item>

              <Text style={{ fontSize: 12 }}>Como se chamam as partes</Text>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                Em branco = Contratante e Contratada. É o nome que sai na ficha e embaixo da
                assinatura.
              </Text>
              <Flex gap={8}>
                <Form.Item name="rotulo_contratante" label="1ª parte" style={{ flex: 1 }}
                  dependencies={['rotulo_contratada']}
                  rules={[({ getFieldValue }) => ({
                    validator(_, v) {
                      const outro = String(getFieldValue('rotulo_contratada') || '').trim();
                      const este = String(v || '').trim();
                      if (!!este !== !!outro) return Promise.reject(new Error('Preencha os dois'));
                      if (este && este.toLowerCase() === outro.toLowerCase()) {
                        return Promise.reject(new Error('Nomes iguais'));
                      }
                      return Promise.resolve();
                    },
                  })]}>
                  <Input placeholder="Contratante" maxLength={40} />
                </Form.Item>
                <Form.Item name="rotulo_contratada" label="2ª parte" style={{ flex: 1 }}
                  dependencies={['rotulo_contratante']}>
                  <Input placeholder="Contratada" maxLength={40} />
                </Form.Item>
              </Flex>
              {emUso > 0 && (
                <Text type="warning" style={{ fontSize: 11, display: 'block', marginTop: -6, marginBottom: 8 }}>
                  Este tipo já tem {emUso} contrato(s): mudar os nomes muda o que a ficha deles diz.
                </Text>
              )}

              <Form.Item name="sensivel" valuePropName="checked" style={{ marginBottom: 4 }}>
                <Checkbox>
                  <Text style={{ fontSize: 12 }}>
                    Tem dado pessoal <Text type="secondary" style={{ fontSize: 11 }}>
                      (pessoa física, CPF, salário, dívida) — a ficha e o PDF ficam só para quem
                      participa do contrato, quem o arquivou e quem vê todos os expedientes; os outros veem a linha
                      sem contraparte nem valor
                    </Text>
                  </Text>
                </Checkbox>
              </Form.Item>
              <Form.Item name="redigivel" valuePropName="checked" style={{ marginBottom: 4 }}>
                <Checkbox disabled={ehDistrato}>
                  <Text style={{ fontSize: 12 }}>Pode ser redigido aqui</Text>
                </Checkbox>
              </Form.Item>
              {tipoEditado && (
                <Form.Item name="ativo" valuePropName="checked">
                  <Checkbox disabled={ehDistrato}>
                    <Text style={{ fontSize: 12 }}>Ativo <Text type="secondary" style={{ fontSize: 11 }}>
                      (desativar tira das listas; os contratos que já existem não mudam)
                    </Text></Text>
                  </Checkbox>
                </Form.Item>
              )}
              {ehDistrato && (
                <Alert type="info" showIcon style={{ fontSize: 12, marginBottom: 12 }}
                  message="O Distrato é usado pelo botão “Distrato” de cada contrato — ele não se desativa." />
              )}

              <Flex justify="space-between" gap={8} wrap style={{ marginTop: 8 }}>
                <Button onClick={() => sairComCuidado(voltarParaLista)}>Voltar</Button>
                <Button type="primary" loading={salvando} disabled={!!gerando} onClick={() => salvar()}>
                  {editando === 'novo' ? 'Cadastrar tipo' : 'Salvar'}
                </Button>
              </Flex>
            </Form>
          </div>

          {/* Coluna do modelo de texto */}
          <div style={{ flex: '2 1 520px', minWidth: 300 }}>
            <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 6 }}>
              <Text style={{ fontSize: 12 }}>Modelo de texto <Text type="secondary" style={{ fontSize: 11 }}>
                (é o texto com que o contrato abre em “Redigir contrato”)
              </Text></Text>
              <Upload accept=".pdf,.docx" showUploadList={false} disabled={!!gerando}
                beforeUpload={(f) => { gerarModelo(f as File); return false; }}>
                <Button size="small" loading={!!gerando}>
                  {gerando === 'subindo' ? 'Enviando…' : gerando === 'lendo' ? 'Lendo o contrato…'
                    : 'Gerar a partir de um contrato (PDF ou Word)'}
                </Button>
              </Upload>
            </Flex>

            {gerando === 'lendo' && (
              <Alert type="info" showIcon style={{ marginBottom: 8 }}
                message={<Space><Spin size="small" />A IA está transcrevendo o contrato e tirando os dados
                  dele — leva de 30 segundos a 2 minutos.</Space>} />
            )}
            {avisoModelo && (
              <Alert type={avisoModelo.truncado ? 'warning' : 'success'} showIcon style={{ marginBottom: 8 }}
                message={`Modelo montado a partir de ${avisoModelo.arquivo} — confira antes de salvar`}
                description={
                  <span style={{ fontSize: 12 }}>
                    Nomes, documentos, valores e datas daquele contrato viram lacunas (________).
                    {avisoModelo.removidos > 0 && (
                      <> O filtro ainda tirou <b>{avisoModelo.removidos}</b> dado(s) específico(s) que a IA
                        tinha deixado — procure lacunas no meio de frases.</>
                    )}
                    {avisoModelo.truncado && (
                      <> <b>O texto veio cortado no fim</b> (contrato longo): confira se as últimas
                        cláusulas estão aí e complete à mão.</>
                    )}
                  </span>
                } />
            )}

            <RichTextEditor
              key={versaoEditor}
              value={corpo}
              onChange={(html) => { corpoRef.current = html; setCorpoMexido(true); }}
              height={Math.max(360, (typeof window !== 'undefined' ? window.innerHeight : 800) - 330)}
            />
          </div>
        </Flex>
      )}
    </Modal>
  );
}
